import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../../components/feature/LanguageSwitcher';
import { WebsiteAnalyzer, AnalysisResult } from '../../services/websiteAnalyzer';
import { useSEO, generateWebPageSchema, generateBreadcrumbSchema } from '../../utils/seoHelpers';

export default function AnalysePage() {
  const { t, i18n } = useTranslation();
  const isNo = i18n.language === 'no';
  const [domain, setDomain] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string>('');

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://norwebas.no';

  // SEO Configuration
  useSEO({
    title: 'Gratis Nettside Analyse | NorWeb AS - Analyser Din Nettside',
    description: 'Få en gratis og detaljert analyse av nettsiden din. Vi sjekker hastighet, SEO, mobilvennlighet og sikkerhet. Få konkrete forbedringsforslag fra NorWeb AS.',
    keywords: 'nettside analyse, gratis analyse, SEO analyse, hastighetstest nettside, mobilvennlig test, nettside sjekk',
    canonical: `${siteUrl}/analyse`,
    ogImage: 'https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/794dc186f9398e7ccf26db530a3381f1.png',
    structuredData: {
      '@context': 'https://schema.org',
      '@graph': [
        generateWebPageSchema({
          name: 'Gratis Nettside Analyse - NorWeb AS',
          description: 'Få en gratis og detaljert analyse av nettsiden din med konkrete forbedringsforslag.',
          url: '/analyse'
        }),
        generateBreadcrumbSchema([
          { name: 'Hjem', url: siteUrl },
          { name: 'Analyse', url: `${siteUrl}/analyse` }
        ]),
        {
          '@type': 'WebApplication',
          'name': 'NorWeb Nettside Analyse',
          'applicationCategory': 'UtilityApplication',
          'offers': {
            '@type': 'Offer',
            'price': '0',
            'priceCurrency': 'NOK'
          },
          'description': 'Gratis verktøy for å analysere nettsider og få forbedringsforslag'
        }
      ]
    }
  });

  // Blocked domains
  const blockedDomains = [
    'norwebas.no',
    'www.norwebas.no',
    'norweb.no',
    'www.norweb.no'
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain.trim()) return;
    
    const cleanDomain = domain
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/\/.*$/, '');
    if (
      blockedDomains.some(
        blocked => cleanDomain === blocked || cleanDomain.endsWith('.' + blocked)
      )
    ) {
      setError(
        isNo
          ? 'Denne nettsiden kan ikke analyseres. Vennligst prøv en annen nettside.'
          : 'This website cannot be analyzed. Please try another website.'
      );
      return;
    }
    
    setIsAnalyzing(true);
    setError('');
    setAnalysisResult(null);

    try {
      const analyzer = new WebsiteAnalyzer(domain.trim());
      const result = await analyzer.analyze();
      setAnalysisResult(result);
      
      setTimeout(() => {
        document.getElementById('analysis-results')?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    } catch (err) {
      setError(t('analyse.error.failed'));
      console.error('Analysis failed:', err);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getScoreColor = (score: number, isPreferred: boolean = false) => {
    if (isPreferred) {
      if (score >= 80) return 'text-green-600';
      if (score >= 60) return 'text-emerald-600';
      return 'text-yellow-600';
    }
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number, isPreferred: boolean = false) => {
    if (isPreferred) {
      if (score >= 80) return 'bg-green-100';
      if (score >= 60) return 'bg-emerald-100';
      return 'bg-yellow-100';
    }
    if (score >= 75) return 'bg-green-100';
    if (score >= 50) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getScoreLabel = (score: number, isPreferred: boolean = false) => {
    if (isPreferred) {
      if (score >= 80) return t('analyse.results.scoreExcellentPreferred');
      if (score >= 60) return t('analyse.results.scoreGoodPreferred');
      return t('analyse.results.scoreFairPreferred');
    }
    if (score >= 75) return t('analyse.results.scoreExcellent');
    if (score >= 50) return t('analyse.results.scoreGood');
    return t('analyse.results.scorePoor');
  };

  const getSummary = (score: number, isPreferred: boolean = false) => {
    if (isPreferred) {
      if (score >= 80) return t('analyse.results.summaryExcellentPreferred');
      if (score >= 60) return t('analyse.results.summaryGoodPreferred');
      return t('analyse.results.summaryFairPreferred');
    }
    if (score >= 75) return t('analyse.results.summaryExcellent');
    if (score >= 50) return t('analyse.results.summaryGood');
    return t('analyse.results.summaryPoor');
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans overflow-x-hidden flex flex-col">
      {/* Skip to main content */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-fuchsia-600 focus:text-white focus:rounded-lg"
      >
        {t('analyse.skipToMain')}
      </a>

      {/* Navigation */}
      <header>
        <nav
          className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
            isScrolled
              ? 'bg-white/95 backdrop-blur-md shadow-lg'
              : 'bg-white/95 backdrop-blur-md shadow-sm'
          }`}
          aria-label={t('analyse.mainNav')}
        >
          <div className="w-full px-4 sm:px-6 lg:px-12 py-3">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center gap-2" aria-label={t('analyse.logoAria')}>
                <img
                  src="https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/794dc186f9398e7ccf26db530a3381f1.png"
                  alt="NorWeb AS logo"
                  className="h-7 sm:h-8 w-auto"
                  width="100"
                  height="32"
                />
              </a>
              <div className="flex items-center gap-3 sm:gap-4">
                <LanguageSwitcher />
                <a
                  href="/#contact-form"
                  className="px-4 py-2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-xs sm:text-sm font-medium rounded-md hover:from-violet-600 hover:to-fuchsia-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  {t('analyse.contactBtn')}
                </a>
              </div>
            </div>
          </div>
        </nav>
      </header>

      <main id="main-content" className="flex-1 flex flex-col">
        {/* Hero Section - fills available space when no results */}
        <section className={`relative flex items-center justify-center px-4 sm:px-6 lg:px-12 pt-20 bg-gradient-to-br from-slate-900 via-violet-900 to-fuchsia-900 ${analysisResult ? 'pb-16 sm:pt-24 sm:pb-20' : 'flex-1 pb-8'}`} aria-labelledby="analyse-hero-title">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE2YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00em0wIDI0YzAtMi4yMSAxLjc5LTQgNC00czQgMS43OSA0IDQtMS43OSA0LTQgNC00LTEuNzktNC00ek0xMiAxNmMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHptMCAyNGMwLTIuMjEgMS43OS00IDQtNHM0IDEuNzkgNCA0LTEuNzkgNC00IDQtNC0xLjc5LTQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"
          aria-hidden="true"
        ></div>

          <div className="relative z-10 w-full max-w-2xl mx-auto text-center">
            <div className="mb-6 sm:mb-8">
              <h1 id="analyse-hero-title" className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 leading-tight px-2">
                {t('analyse.hero.title')}
              </h1>
              <p className="text-sm sm:text-base text-violet-200 mb-6 max-w-xl mx-auto px-4">
                {t('analyse.hero.subtitle')}
              </p>
            </div>

            {/* Domain Input Form */}
            <form onSubmit={handleSubmit} className="max-w-xl mx-auto px-2">
              <div className="bg-white rounded-xl shadow-2xl p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <label htmlFor="domain-input" className="sr-only">
                      {t('analyse.hero.inputLabel')}
                    </label>
                    <input
                      id="domain-input"
                      type="text"
                      value={domain}
                      onChange={e => setDomain(e.target.value)}
                      placeholder={t('analyse.hero.placeholder')}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-200 outline-none transition-all text-sm sm:text-base text-slate-800"
                      disabled={isAnalyzing}
                      required
                      aria-required="true"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isAnalyzing || !domain.trim()}
                    className="px-5 py-3 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-medium rounded-lg hover:from-violet-700 hover:to-fuchsia-700 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base flex items-center justify-center gap-2"
                    aria-busy={isAnalyzing}
                  >
                    {isAnalyzing ? (
                      <>
                        <i className="ri-loader-4-line animate-spin" aria-hidden="true"></i>
                        <span>{t('analyse.hero.analyzing')}</span>
                      </>
                    ) : (
                      <>
                        <i className="ri-search-line" aria-hidden="true"></i>
                        <span>{t('analyse.hero.ctaButton')}</span>
                      </>
                    )}
                  </button>
                </div>

                {error && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs sm:text-sm text-left" role="alert">
                    <i className="ri-error-warning-line mr-1" aria-hidden="true"></i>
                    {error}
                  </div>
                )}

                <p className="text-xs sm:text-sm text-slate-500 mt-3 text-left">
                  <i className="ri-shield-check-line text-fuchsia-600 mr-1" aria-hidden="true"></i>
                  {t('analyse.hero.privacy')}
                </p>
              </div>
            </form>

            {/* Trust Indicators */}
            <div className="mt-8 sm:mt-10 grid grid-cols-3 gap-4 sm:gap-6 max-w-lg mx-auto px-2">
              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-full flex items-center justify-center mb-2" aria-hidden="true">
                  <i className="ri-flashlight-line text-lg sm:text-xl text-fuchsia-300"></i>
                </div>
                <h3 className="text-xs sm:text-sm font-medium text-white mb-1">{t('analyse.hero.feature1Title')}</h3>
                <p className="text-[10px] sm:text-xs text-violet-200 leading-tight">{t('analyse.hero.feature1Desc')}</p>
              </div>

              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-full flex items-center justify-center mb-2" aria-hidden="true">
                  <i className="ri-shield-check-line text-lg sm:text-xl text-fuchsia-300"></i>
                </div>
                <h3 className="text-xs sm:text-sm font-medium text-white mb-1">{t('analyse.hero.feature2Title')}</h3>
                <p className="text-[10px] sm:text-xs text-violet-200 leading-tight">{t('analyse.hero.feature2Desc')}</p>
              </div>

              <div className="flex flex-col items-center text-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/10 rounded-full flex items-center justify-center mb-2" aria-hidden="true">
                  <i className="ri-file-chart-line text-lg sm:text-xl text-fuchsia-300"></i>
                </div>
                <h3 className="text-xs sm:text-sm font-medium text-white mb-1">{t('analyse.hero.feature3Title')}</h3>
                <p className="text-[10px] sm:text-xs text-violet-200 leading-tight">{t('analyse.hero.feature3Desc')}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Analysis Results Section */}
        {analysisResult && (
          <section id="analysis-results" className="py-10 sm:py-14 px-4 sm:px-6 lg:px-12 bg-slate-50">
            <div className="max-w-4xl mx-auto">
              {/* Total Score */}
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6 mb-8">
                <div className="text-center mb-4">
                  <h2 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">{t('analyse.results.title')}</h2>
                  <p className="text-xs text-slate-600">{analysisResult.domain}</p>
                </div>

                <div className="flex flex-col items-center">
                  <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full ${getScoreBgColor(analysisResult.totalScore, analysisResult.isPreferredDomain)} flex items-center justify-center mb-3`}>
                    <div className="text-center">
                      <div className={`text-2xl sm:text-3xl font-bold ${getScoreColor(analysisResult.totalScore, analysisResult.isPreferredDomain)}`}>
                        {analysisResult.totalScore}
                      </div>
                      <div className="text-[10px] text-slate-600 font-medium">/ 100</div>
                    </div>
                  </div>

                  <h3 className={`text-sm sm:text-base font-bold ${getScoreColor(analysisResult.totalScore, analysisResult.isPreferredDomain)} mb-1`}>
                    {getScoreLabel(analysisResult.totalScore, analysisResult.isPreferredDomain)}
                  </h3>

                  <p className="text-xs text-slate-600 text-center max-w-xl px-2">
                    {getSummary(analysisResult.totalScore, analysisResult.isPreferredDomain)}
                  </p>
                </div>

                {/* Metrics Breakdown */}
                <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(analysisResult.metrics).map(([key, value]) => (
                    <div key={key} className="text-center">
                      <div className="text-lg sm:text-xl font-bold text-slate-900 mb-0.5">{value}</div>
                      <div className="text-[10px] sm:text-xs text-slate-600">{t(`analyse.results.metrics.${key}`)}</div>
                      <div className="mt-1 w-full bg-slate-200 rounded-full h-1 overflow-hidden">
                        <div
                          className={`h-1 rounded-full transition-all duration-1000 ease-out ${
                            analysisResult.isPreferredDomain
                              ? value >= 70
                                ? 'bg-green-500'
                                : value >= 50
                                ? 'bg-emerald-500'
                                : 'bg-yellow-500'
                              : value >= 80
                              ? 'bg-green-500'
                              : value >= 60
                              ? 'bg-yellow-500'
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${value}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Critical Issues */}
              {analysisResult.issues.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-3">
                    {analysisResult.isPreferredDomain
                      ? t('analyse.results.issuesTitlePreferred')
                      : t('analyse.results.issuesTitle')}
                  </h2>

                  <div className="space-y-3">
                    {analysisResult.issues.map(issue => (
                      <div
                        key={issue.id}
                        className="bg-white rounded-lg shadow-sm p-3 sm:p-4 border-l-3"
                        style={{
                          borderLeftColor: analysisResult.isPreferredDomain
                            ? issue.severity === 'critical'
                              ? '#f59e0b'
                              : issue.severity === 'warning'
                              ? '#10b981'
                              : '#3b82f6'
                            : issue.severity === 'critical'
                            ? '#ef4444'
                            : issue.severity === 'warning'
                            ? '#f59e0b'
                            : '#3b82f6'
                        }}
                      >
                        <div className="flex items-start gap-2 sm:gap-3">
                          <div
                            className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                              analysisResult.isPreferredDomain
                                ? issue.severity === 'critical'
                                  ? 'bg-yellow-100'
                                  : issue.severity === 'warning'
                                  ? 'bg-emerald-100'
                                  : 'bg-blue-100'
                                : issue.severity === 'critical'
                                ? 'bg-red-100'
                                : issue.severity === 'warning'
                                ? 'bg-yellow-100'
                                : 'bg-blue-100'
                            }`}
                          >
                            <i
                              className={`text-sm ${
                                analysisResult.isPreferredDomain
                                  ? issue.severity === 'critical'
                                    ? 'ri-lightbulb-line text-yellow-600'
                                    : issue.severity === 'warning'
                                    ? 'ri-lightbulb-line text-emerald-600'
                                    : 'ri-information-line text-blue-600'
                                  : issue.severity === 'critical'
                                  ? 'ri-error-warning-line text-red-600'
                                  : issue.severity === 'warning'
                                  ? 'ri-alert-line text-yellow-600'
                                  : 'ri-information-line text-blue-600'
                              }`}
                              aria-hidden="true"
                            ></i>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5 mb-1">
                              <h3 className="text-xs sm:text-sm font-bold text-slate-900">
                                {isNo ? issue.titleNo : issue.titleEn}
                              </h3>
                              <span
                                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                                  analysisResult.isPreferredDomain
                                    ? issue.severity === 'critical'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : issue.severity === 'warning'
                                      ? 'bg-emerald-100 text-emerald-700'
                                      : 'bg-blue-100 text-blue-700'
                                    : issue.severity === 'critical'
                                    ? 'bg-red-100 text-red-700'
                                    : issue.severity === 'warning'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {issue.score}/100
                              </span>
                            </div>
                            <p className="text-[11px] sm:text-xs text-slate-700 mb-2">
                              {isNo ? issue.descriptionNo : issue.descriptionEn}
                            </p>
                            <div
                              className={`${
                                analysisResult.isPreferredDomain ? 'bg-amber-50/60' : 'bg-red-50/60'
                              } rounded-md p-2`}
                            >
                              <p className={`text-[10px] ${analysisResult.isPreferredDomain ? 'text-amber-800' : 'text-red-800'} font-medium mb-0.5`}>
                                <i className={`${analysisResult.isPreferredDomain ? 'ri-lightbulb-line' : 'ri-error-warning-line'} mr-1`} aria-hidden="true"></i>
                                {analysisResult.isPreferredDomain
                                  ? t('analyse.results.whyItMatters')
                                  : t('analyse.results.impact')}
                              </p>
                              <p className={`text-[10px] ${analysisResult.isPreferredDomain ? 'text-amber-700' : 'text-red-700'}`}>
                                {isNo ? issue.impactNo : issue.impactEn}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Why Choose Us */}
              <div className="mb-8">
                <div className="text-center mb-5">
                  <h2 className="text-base sm:text-lg font-bold text-slate-900 mb-1">
                    {t('analyse.whyChooseUs.title')}
                  </h2>
                  <p className="text-xs text-slate-600 max-w-xl mx-auto px-2">
                    {t('analyse.whyChooseUs.subtitle')}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="w-8 h-8 rounded-full bg-fuchsia-100 flex items-center justify-center mb-2">
                      <i className="ri-line-chart-line text-sm text-fuchsia-600" aria-hidden="true"></i>
                    </div>
                    <h3 className="text-xs font-bold text-slate-900 mb-1">{t('analyse.whyChooseUs.benefit1Title')}</h3>
                    <p className="text-[11px] text-slate-600 leading-relaxed">{t('analyse.whyChooseUs.benefit1Desc')}</p>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
                      <i className="ri-speed-line text-sm text-emerald-600" aria-hidden="true"></i>
                    </div>
                    <h3 className="text-xs font-bold text-slate-900 mb-1">{t('analyse.whyChooseUs.benefit2Title')}</h3>
                    <p className="text-[11px] text-slate-600 leading-relaxed">{t('analyse.whyChooseUs.benefit2Desc')}</p>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center mb-2">
                      <i className="ri-smartphone-line text-sm text-violet-600" aria-hidden="true"></i>
                    </div>
                    <h3 className="text-xs font-bold text-slate-900 mb-1">{t('analyse.whyChooseUs.benefit3Title')}</h3>
                    <p className="text-[11px] text-slate-600 leading-relaxed">{t('analyse.whyChooseUs.benefit3Desc')}</p>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center mb-2">
                      <i className="ri-cursor-line text-sm text-amber-600" aria-hidden="true"></i>
                    </div>
                    <h3 className="text-xs font-bold text-slate-900 mb-1">{t('analyse.whyChooseUs.benefit4Title')}</h3>
                    <p className="text-[11px] text-slate-600 leading-relaxed">{t('analyse.whyChooseUs.benefit4Desc')}</p>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center mb-2">
                      <i className="ri-bar-chart-box-line text-sm text-rose-600" aria-hidden="true"></i>
                    </div>
                    <h3 className="text-xs font-bold text-slate-900 mb-1">{t('analyse.whyChooseUs.benefit5Title')}</h3>
                    <p className="text-[11px] text-slate-600 leading-relaxed">{t('analyse.whyChooseUs.benefit5Desc')}</p>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center mb-2">
                      <i className="ri-customer-service-2-line text-sm text-teal-600" aria-hidden="true"></i>
                    </div>
                    <h3 className="text-xs font-bold text-slate-900 mb-1">{t('analyse.whyChooseUs.benefit6Title')}</h3>
                    <p className="text-[11px] text-slate-600 leading-relaxed">{t('analyse.whyChooseUs.benefit6Desc')}</p>
                  </div>
                </div>

                <div className="text-center">
                  <a
                    href="/booking"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-medium rounded-md hover:from-violet-700 hover:to-fuchsia-700 transition-all whitespace-nowrap cursor-pointer text-sm"
                  >
                    <i className="ri-calendar-line" aria-hidden="true"></i>
                    {t('analyse.whyChooseUs.ctaPrimary')}
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-4 px-4 sm:px-6 lg:px-12" role="contentinfo">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <a href="/" className="flex items-center gap-2">
              <img
                src="https://static.readdy.ai/image/aeaa191b0ca02d38c9577b82b13368df/794dc186f9398e7ccf26db530a3381f1.png"
                alt="NorWeb AS logo"
                className="h-6 w-auto brightness-0 invert"
                width="80"
                height="24"
              />
            </a>
            <nav aria-label="Footer navigasjon">
              <ul className="flex items-center gap-4 text-xs text-slate-400 list-none p-0 m-0">
                <li>
                  <a href="/" className="hover:text-white transition-colors cursor-pointer">{t('nav.services')}</a>
                </li>
                <li>
                  <a href="/booking" className="hover:text-white transition-colors cursor-pointer">{t('nav.booking')}</a>
                </li>
                <li>
                  <a href="/#contact-form" className="hover:text-white transition-colors cursor-pointer">{t('nav.contact')}</a>
                </li>
              </ul>
            </nav>
            <p className="text-[11px] text-slate-500">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
